/*
================================================================================
FILE HEADER — apps/studio-web/src/App.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed             |
|----|------------|-------------------|--------------------------|--------------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0 bootstrap        | Created Plex Studio root |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Drag-to-reorder          | handleReorderStep        |
| 3  | 2026-06-25 | Claude Sonnet 4.6 | Execution + Reports      | Full execution state     |
|    |            |                   |                          | machine; shared mirror   |
|    |            |                   |                          | layout; ExecutionPanel + |
|    |            |                   |                          | ReportsPage wired in     |
| 4  | 2026-06-25 | Claude Sonnet 4.6 | Run Config Sheet         | Browser selector, step   |
|    |            |                   | (Option A / Section 25)  | delay, timeout, stop-on- |
|    |            |                   |                          | failure; RunConfig ref   |
|    |            |                   |                          | threads through exec loop|
| 5  | 2026-06-25 | Claude Sonnet 4.6 | Option B — per-step      | RunnerBridge WS client;  |
|    |            |                   | screenshots              | relay step screenshots → |
|    |            |                   |                          | BrowserMirror overlay;   |
|    |            |                   |                          | extension step snapshots |
| 6  | 2026-06-25 | Claude Sonnet 4.6 | Options C+D — Custom     | Element capture mode     |
|    |            |                   | Commands + Locator Editor| (CAPTURE_ELEMENT_AT);    |
|    |            |                   |                          | handleEditCandidates;    |
|    |            |                   |                          | handleAddSteps           |
| 7  | 2026-07-02 | Claude Sonnet 4.6 | Functions tab (Phase 3)  | Added 'functions' to    |
|    |            |                   |                          | StudioTab; FunctionsPage |
|    |            |                   |                          | render; handleEditFn +  |
|    |            |                   |                          | handleSaveFunction       |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/main.tsx

EXPORTS COVERED
---------------
- StudioTab  — union type for all left-nav tabs
- App        — root component

CALLER → CALLEE MAP
--------------------
App renders:
  LeftNav           → tab switching
  BrowserMirror     → always mounted; display:none on tests/reports tabs
  RecorderPanel     → record tab right panel
  TestLibrary       → tests tab (full width)
  ExecutionPanel    → execution tab right panel
  ReportsPage       → reports tab (full width)

App uses:
  ExtensionBridge   → bridge messages
  rawEventToStep()  → STEP_RECORDED → IR step
  buildTestIR()     → wraps steps into TestIR for export
  db.runs.add()     → save run record after execution completes

IMPORTANT POINTS
----------------
1. Mirror is ALWAYS mounted (BrowserMirror never unmounts once rendered).
   Switching to tests/reports hides it via display:none on the wrapper div.
   Frames still arrive and are painted to the canvas while hidden — instant
   re-appearance when switching back to record or execution tab.
2. Execution loop runs entirely in App.tsx to share pendingStepRef with
   the recorder replay flow. executionStopRef + executionPauseRef are refs
   (not state) so the async loop can read them without stale closure issues.
3. On "Run Selected": tests are queued, tab switches to execution, and the
   user must click ▶ Play to start. This matches Mimiq's UX exactly.
4. handleReplay (RecorderPanel) does NOT save to db.runs — only the
   Execution flow saves run records. This preserves the intent distinction.
================================================================================
*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { ExtensionBridge, isExtensionAvailable } from './extension/bridge.js';
import type { BridgeStatus } from './extension/bridge.js';
import type { RecordMode, RawRecordedEvent } from './extension/protocol.js';
import type { IRStep, TestIR, Candidate, Selector } from '@plex/ir';
import { newId } from '@plex/ir';
import { rawEventToStep, buildTestIR as _buildTestIR, stepLabel as _stepLabel } from './ir/builder.js';
import { BrowserMirror } from './components/BrowserMirror.js';
import { LeftNav } from './components/LeftNav.js';
import { RecorderPanel } from './components/RecorderPanel.js';
import { ConnectionDoctor } from './components/ConnectionDoctor.js';
import { TabPicker } from './components/TabPicker.js';
import { SaveTestModal } from './components/SaveTestModal.js';
import { TestLibrary } from './components/TestLibrary.js';
import { ExecutionPanel } from './components/ExecutionPanel.js';
import { ReportsPage } from './components/ReportsPage.js';
import { RunConfigSheet, DEFAULT_RUN_CONFIG } from './components/RunConfigSheet.js';
import type { RunConfig } from './components/RunConfigSheet.js';
import type { NetworkEvent } from './components/RecorderPanel.js';
import { RunnerBridge } from './lib/runnerBridge.js';
import type { RelayStatus } from './lib/runnerBridge.js';
import { db, type DBTest, type DBRun, type DBRunTest, type DBRunStep, type DBFunction } from './lib/db.js';
import type { TabInfo } from './extension/protocol.js';
import type { ExecutionQueueItem, ExecutionRunStatus } from './components/ExecutionPanel.js';
import { SettingsPage } from './components/SettingsPage.js';
import { FunctionsPage } from './components/FunctionsPage.js';
import { loadSettings } from './lib/settings.js';
import { triageFailure, draftSteps } from './lib/ollama.js';
import type { AiTriageEntry } from './components/ExecutionPanel.js';

export type StudioTab = 'record' | 'tests' | 'execution' | 'reports' | 'functions' | 'settings';

const RECORDER_STEP_TIMEOUT_MS = 6000; // used only for the RecorderPanel quick-replay

export function App() {
  const bridge = useRef(new ExtensionBridge()).current;

  // ── Bridge / mirror state ─────────────────────────────────────────────────
  const [bridgeStatus,    setBridgeStatus]    = useState<BridgeStatus>('disconnected');
  const [activeTabId,     setActiveTabId]     = useState(-1);
  const [activeTabTitle,  setActiveTabTitle]  = useState('');
  const [mode,            setMode]            = useState<RecordMode>('drive');
  // Ref keeps mode current inside bridge.on stale closures (effect deps = [bridge, isRecording])
  const modeRef = useRef<RecordMode>('drive');
  // AI Assist mode (Phase 6) — a studio-side UI overlay, NOT a distinct
  // extension RecordMode. The underlying bridge `mode` stays whatever it was
  // (normally 'drive') so 🎯 Pick Element (CAPTURE_ELEMENT_AT) keeps working.
  const [assistModeActive, setAssistModeActive] = useState(false);
  const [isMirroring,     setIsMirroring]     = useState(false);
  const [isRecording,     setIsRecording]     = useState(false);
  const [captureSize,     setCaptureSize]     = useState({ width: 1280, height: 800 });
  const [tabs,            setTabs]            = useState<TabInfo[]>([]);
  const [showTabPicker,   setShowTabPicker]   = useState(false);
  const [isAttaching,     setIsAttaching]     = useState(false);
  const [attachingTitle,  setAttachingTitle]  = useState('');
  const frameCountRef = useRef(0);
  const [frameCount,  setFrameCount]  = useState(0);

  // ── Recorder state ────────────────────────────────────────────────────────
  const [steps,          setSteps]          = useState<IRStep[]>([]);
  const [replayingIndex, setReplayingIndex] = useState<number | null>(null);
  const [stepStatuses,   setStepStatuses]   = useState<Record<string, 'ok' | 'not_found' | 'error'>>({});
  const pendingStepRef = useRef<Map<string, (status: 'ok' | 'not_found' | 'error') => void>>(new Map());

  // ── Studio tabs ───────────────────────────────────────────────────────────
  const [activeStudioTab, setActiveStudioTab] = useState<StudioTab>('record');
  const [showSaveModal,       setShowSaveModal]       = useState(false);
  const [loadedTestId,        setLoadedTestId]        = useState<string | null>(null);
  const [loadedTestName,      setLoadedTestName]      = useState<string>('');
  const [editingFunctionId,   setEditingFunctionId]   = useState<string | null>(null);
  const [editingFunctionName, setEditingFunctionName] = useState<string>('');
  const unsaved = steps.length > 0 && !loadedTestId && !editingFunctionId;

  // ── Network events ────────────────────────────────────────────────────────
  const [networkEvents, setNetworkEvents] = useState<NetworkEvent[]>([]);

  // ── Highlighter (always-on interaction visual feedback) ───────────────────
  const [highlighterEnabled, setHighlighterEnabled] = useState(true);
  const [highlighterColor,   setHighlighterColor]   = useState('#7c3aed');

  // ── Inspect mode — captured element from clicking in Inspect tab ──────────
  const [inspectedNode, setInspectedNode] = useState<import('./extension/protocol.js').SerializedAXNode | null>(null);

  // ── Execution state ───────────────────────────────────────────────────────
  const [executionQueue,       setExecutionQueue]       = useState<ExecutionQueueItem[]>([]);
  const [executionStatus,      setExecutionStatus]      = useState<ExecutionRunStatus>('idle');
  const [executionPassedTests, setExecutionPassedTests] = useState(0);
  const [executionFailedTests, setExecutionFailedTests] = useState(0);
  const executionStopRef       = useRef(false);
  const executionPauseRef      = useRef(false);
  const executionLoopActiveRef = useRef(false); // true while async loop is running

  // ── Run Config Sheet ──────────────────────────────────────────────────────
  // Seed from saved settings so the RunConfigSheet opens with the user's defaults.
  // loadSettings() reads localStorage once at mount; the ref is stable thereafter.
  const runConfigRef = useRef<RunConfig>((() => {
    const s = loadSettings();
    return {
      ...DEFAULT_RUN_CONFIG,
      browser:            s.defaultBrowser,
      headless:           s.defaultHeadless,
      stepDelayMs:        s.defaultStepDelayMs,
      stepTimeoutMs:      s.defaultStepTimeoutMs,
      stopOnFirstFailure: s.defaultStopOnFirstFailure,
    };
  })());
  const [showRunConfigSheet,  setShowRunConfigSheet]  = useState(false);
  const [pendingRunTests,     setPendingRunTests]     = useState<import('./lib/db.js').DBTest[]>([]);

  // ── Tier-2 AI state ──────────────────────────────────────────────────────
  // aiTriageResults: keyed by step.id — shows AI analysis below each failed step
  const [aiTriageResults, setAiTriageResults] = useState<Map<string, AiTriageEntry>>(new Map());
  // draftStepsResult: a single state for "Suggest Steps" in the recorder
  const [draftStepsResult, setDraftStepsResult] = useState<{ status: 'idle' | 'loading' | 'done' | 'error'; text: string }>({ status: 'idle', text: '' });
  // pending ARIA tree from CAPTURE_TREE — used by Tier-2 handlers
  const pendingAriaCb = useRef<((tree: import('./extension/protocol.js').SerializedAXTree | null) => void) | null>(null);
  const pendingScreenshotCb = useRef<((dataUrl: string | null) => void) | null>(null);
  // Last ERROR message from the extension bridge — surfaced by Tier-2 handlers
  // instead of a generic timeout guess when CAPTURE_TREE/CAPTURE_SCREENSHOT fails
  // (e.g. debugger not attached). Cleared once consumed.
  const lastBridgeErrorRef = useRef<string | null>(null);

  // ── CLI Relay (RunnerBridge) ──────────────────────────────────────────────
  // One singleton RunnerBridge per Studio session; connects to `plex run --studio-relay`
  const runnerBridge            = useRef(new RunnerBridge()).current;
  const [relayStatus,         setRelayStatus]         = useState<RelayStatus>('disconnected');
  // Current step screenshot shown in the mirror (null = live canvas / extension frames)
  const [stepScreenshotUrl,   setStepScreenshotUrl]   = useState<string | null>(null);
  const [stepScreenshotLabel, setStepScreenshotLabel] = useState<string | undefined>(undefined);

  // ─────────────────────────────────────────────────────────────────────────
  // Bridge wiring
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = bridge.onStatus(setBridgeStatus);
    return unsub;
  }, [bridge]);

  useEffect(() => {
    const unsub = bridge.on((msg) => {
      switch (msg.type) {
        case 'ATTACHED':
          setActiveTabId(msg.tabId);
          setActiveTabTitle(msg.title);
          setIsAttaching(false);
          setIsMirroring(true);
          bridge.send({ type: 'START_MIRROR' });
          break;
        case 'DETACHED':
          setActiveTabId(-1);
          setActiveTabTitle('');
          setIsMirroring(false);
          setIsRecording(false);
          break;
        case 'STEP_RECORDED':
          if (isRecording) {
            const step = rawEventToStep(msg.step as RawRecordedEvent);
            if (step) setSteps((prev) => [...prev, step]);
          }
          break;
        case 'STEP_RESULT': {
          const resolve = pendingStepRef.current.get(msg.stepId);
          if (resolve) { resolve(msg.status); pendingStepRef.current.delete(msg.stepId); }
          setStepStatuses(prev => ({ ...prev, [msg.stepId]: msg.status }));
          break;
        }
        case 'MIRROR_FRAME':
          frameCountRef.current++;
          if (frameCountRef.current % 20 === 0) setFrameCount(frameCountRef.current);
          break;
        case 'TABS_LIST':
          setTabs(msg.tabs);
          setShowTabPicker(true);
          break;
        case 'TREE_SNAPSHOT':
          if (msg.tree.viewportWidth) {
            setCaptureSize({ width: msg.tree.viewportWidth, height: msg.tree.viewportHeight });
          }
          // Tier-2: resolve any pending ARIA tree request
          if (pendingAriaCb.current) {
            pendingAriaCb.current(msg.tree);
            pendingAriaCb.current = null;
          }
          break;
        case 'ARIA_NODE':
          // Content-script inspect click (via SET_MODE inspect path)
          setInspectedNode(msg.node);
          break;
        case 'SCREENSHOT':
          // Extension sent a screenshot — update mirror overlay during execution
          if (activeStudioTab === 'execution') {
            setStepScreenshotUrl(msg.dataUrl);
          }
          // Tier-2: resolve any pending screenshot request
          if (pendingScreenshotCb.current) {
            pendingScreenshotCb.current(msg.dataUrl);
            pendingScreenshotCb.current = null;
          }
          break;
        case 'ELEMENT_CAPTURED': {
          const node = msg.node;

          // Route 1: Inspect mode — use modeRef (NOT mode state) to avoid stale closure.
          // The bridge.on effect is created with [bridge, isRecording] deps; if mode state
          // changes after the effect runs, `mode` in the closure is STALE. modeRef.current
          // always reflects the current mode regardless of when the effect was created.
          if (modeRef.current === 'inspect') {
            setInspectedNode(node);
            break;
          }

          // Route 2: Command Builder capture (also used by AI Assist's 🎯 Pick
          // Element, which reuses this exact same capture mechanism) — build
          // Selector and fire callback.
          let sel: Selector | null = null;
          if (node) {
            const candidates: Candidate[] = [];
            const attrs = (node as { attributes?: Record<string, string> }).attributes ?? {};
            const testId = attrs['data-testid'] ?? attrs['data-test-id'] ?? attrs['data-cy'];
            if (testId)          candidates.push({ strategy: 'a11yId',      value: testId,          addedBy: 'recorder' });
            if (node.name)       candidates.push({ strategy: 'text',        value: node.name,       addedBy: 'recorder' });
            if (node.description && node.description !== node.name) candidates.push({ strategy: 'contentDesc', value: node.description, addedBy: 'recorder' });
            if (candidates.length === 0) candidates.push({ strategy: 'xpath', value: `//div[@role="${node.role}"]`, addedBy: 'recorder' });
            sel = { description: node.name ?? node.description ?? node.role ?? 'element', candidates };

            // "✓ Element Captured" pulse on the mirror — a NEW object every
            // time so BrowserMirror's effect re-triggers even for identical bounds.
            if (node.bounds) setElementCapturedBounds({ ...node.bounds });
          }
          commandCaptureCallbackRef.current?.(sel);
          commandCaptureCallbackRef.current = null;
          setCommandCaptureActive(false);
          break;
        }
        case 'NETWORK_EVENT':
          setNetworkEvents(prev => [...prev.slice(-99), {
            requestId:   msg.requestId,
            method:      msg.method,
            url:         msg.url,
            status:      msg.status,
            durationMs:  msg.durationMs,
            requestBody: msg.requestBody,
            mimeType:    msg.mimeType,
            capturedAt:  Date.now(),
          }]);
          break;
        case 'ERROR':
          console.error('[Plex bridge error]', msg.message);
          lastBridgeErrorRef.current = msg.message;
          // A CAPTURE_TREE/CAPTURE_SCREENSHOT request that can't be fulfilled
          // (e.g. debugger not attached) arrives as ERROR, not TREE_SNAPSHOT/
          // SCREENSHOT — resolve immediately instead of waiting out the 5s timeout.
          if (pendingAriaCb.current) {
            pendingAriaCb.current(null);
            pendingAriaCb.current = null;
          }
          if (pendingScreenshotCb.current) {
            pendingScreenshotCb.current(null);
            pendingScreenshotCb.current = null;
          }
          break;
      }
    });
    return unsub;
  }, [bridge, isRecording]);

  // Re-attach after SW restart
  useEffect(() => {
    const unsub = bridge.onStatus((s) => {
      if (s === 'connected' && activeTabId >= 0) {
        bridge.send({ type: 'ATTACH', tabId: activeTabId });
      }
    });
    return unsub;
  }, [bridge, activeTabId]);

  // Auto-connect on mount
  useEffect(() => {
    if (isExtensionAvailable()) bridge.connect();
    return () => bridge.disconnect();
  }, [bridge]);

  // ── RunnerBridge (CLI relay) wiring ───────────────────────────────────────
  useEffect(() => {
    const unsubStatus = runnerBridge.onStatus(setRelayStatus);
    const unsubMsg    = runnerBridge.on((msg) => {
      switch (msg.type) {
        case 'RELAY_RUN_STARTED':
          // Clear any previous step screenshot when a new run starts
          setStepScreenshotUrl(null);
          setStepScreenshotLabel(undefined);
          break;
        case 'RELAY_STEP_STARTED':
          setStepScreenshotLabel(`Step ${msg.stepIndex + 1}/${msg.total} — ${msg.stepType} ${msg.label}`);
          break;
        case 'RELAY_STEP_SCREENSHOT':
          setStepScreenshotUrl(msg.dataUrl);
          setStepScreenshotLabel(prev => prev); // keep existing label
          break;
        case 'RELAY_STEP_COMPLETED':
          // Update execution queue with step result from relay
          setExecutionQueue(prev => prev.map(item =>
            item.steps.some(s => s.id === msg.stepId)
              ? { ...item, stepStatuses: { ...item.stepStatuses, [msg.stepId]: msg.status } }
              : item
          ));
          break;
        case 'RELAY_RUN_COMPLETED':
          setExecutionStatus('done');
          setStepScreenshotLabel(`Run complete — ${msg.passedCount} passed · ${msg.failedCount} failed`);
          break;
      }
    });
    return () => { unsubStatus(); unsubMsg(); };
  }, [runnerBridge]);

  // ── Element capture for CommandBuilder ────────────────────────────────────
  // When commandCaptureActive is true, BrowserMirror sends CAPTURE_ELEMENT_AT
  // on click. The ELEMENT_CAPTURED response routes to commandCaptureCallbackRef.
  const [commandCaptureActive,  setCommandCaptureActive]  = useState(false);
  const commandCaptureCallbackRef = useRef<((sel: Selector | null) => void) | null>(null);
  // "✓ Element Captured" pulse on the mirror (AI Assist 🎯 Pick Element feature request)
  const [elementCapturedBounds, setElementCapturedBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const handleRequestCapture = useCallback((callback: (sel: Selector | null) => void) => {
    commandCaptureCallbackRef.current = callback;
    setCommandCaptureActive(true);
  }, []);

  const handleConnectRelay    = useCallback(() => { runnerBridge.connect(); }, [runnerBridge]);
  const handleDisconnectRelay = useCallback(() => {
    runnerBridge.disconnect();
    setStepScreenshotUrl(null);
    setStepScreenshotLabel(undefined);
  }, [runnerBridge]);

  // ─────────────────────────────────────────────────────────────────────────
  // Mirror handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleConnect = useCallback(() => {
    if (bridgeStatus === 'error' || bridgeStatus === 'disconnected') bridge.connect();
  }, [bridge, bridgeStatus]);

  const handleStartMirror = useCallback(() => {
    bridge.send({ type: 'GET_TABS' });
  }, [bridge]);

  const handleTabPick = useCallback((tab: TabInfo) => {
    setShowTabPicker(false);
    setIsAttaching(true);
    setAttachingTitle(tab.title || tab.url);
    bridge.send({ type: 'ATTACH', tabId: tab.id });
  }, [bridge]);

  const handleStopMirror = useCallback(() => {
    bridge.send({ type: 'STOP_MIRROR' });
    setIsMirroring(false);
    setIsRecording(false);
    frameCountRef.current = 0;
    setFrameCount(0);
  }, [bridge]);

  // ─────────────────────────────────────────────────────────────────────────
  // Recorder handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleSetMode = useCallback((m: RecordMode) => {
    setMode(m);
    modeRef.current = m; // keep ref in sync for stale closures
    bridge.send({ type: 'SET_MODE', mode: m });
    if (m !== 'inspect') setInspectedNode(null);
  }, [bridge]);

  const handleToggleRecord = useCallback(() => {
    if (!isRecording) {
      if (activeTabId >= 0) {
        setIsRecording(true);
      } else {
        bridge.send({ type: 'GET_TABS' });
      }
    } else {
      setIsRecording(false);
    }
  }, [bridge, isRecording, activeTabId]);

  const handleDelete = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
    setLoadedTestId(null);
  }, []);

  const handleEditStep = useCallback((index: number, patch: Record<string, unknown>) => {
    setSteps((prev) => prev.map((s, i) => i === index ? ({ ...s, ...patch } as IRStep) : s));
    setLoadedTestId(null);
  }, []);

  const handleReorderStep = useCallback((from: number, to: number) => {
    setSteps((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      if (moved) arr.splice(to, 0, moved);
      return arr;
    });
    setLoadedTestId(null);
  }, []);

  // Called by StepEditor when the user edits/reorders/adds/removes candidates
  const handleEditCandidates = useCallback((index: number, candidates: Candidate[]) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== index) return s;
      if (!('selector' in s) || !s.selector) return s;
      return { ...s, selector: { ...(s.selector as { description: string; candidates: Candidate[] }), candidates } } as IRStep;
    }));
    setLoadedTestId(null);
  }, []);

  // Called by CustomCommandsPanel when user clicks "+ Add Step"
  const handleAddSteps = useCallback((newSteps: IRStep[]) => {
    setSteps((prev) => [...prev, ...newSteps]);
    setLoadedTestId(null);
  }, []);

  const handleLoadFromLibrary = useCallback((loadedSteps: IRStep[], testId: string, testName: string) => {
    setSteps(loadedSteps);
    setStepStatuses({});
    setLoadedTestId(testId);
    setLoadedTestName(testName);
    setEditingFunctionId(null);
    setEditingFunctionName('');
    setActiveStudioTab('record');
  }, []);

  // Load a Function into the recorder for editing
  const handleEditFunctionInRecorder = useCallback((fn: DBFunction) => {
    try {
      const fnSteps = JSON.parse(fn.irJson) as IRStep[];
      setSteps(fnSteps);
    } catch {
      setSteps([]);
    }
    setStepStatuses({});
    setLoadedTestId(null);
    setLoadedTestName('');
    setEditingFunctionId(fn.id);
    setEditingFunctionName(fn.name);
    setActiveStudioTab('record');
  }, []);

  // Save current recorder steps back to the function being edited
  const handleSaveFunction = useCallback(async () => {
    if (!editingFunctionId) return;
    await db.functions.update(editingFunctionId, {
      irJson: JSON.stringify(steps),
      stepCount: steps.length,
      updatedAt: Date.now(),
    });
  }, [editingFunctionId, steps]);

  // ── Tier-2 helpers ────────────────────────────────────────────────────────

  /** Request a fresh ARIA tree from the extension, resolving via pending callback. */
  const requestAriaTree = useCallback((): Promise<import('./extension/protocol.js').SerializedAXTree | null> => {
    return new Promise((resolve) => {
      pendingAriaCb.current = resolve;
      bridge.send({ type: 'CAPTURE_TREE' });
      // 5s timeout — if extension doesn't respond, resolve null
      setTimeout(() => { if (pendingAriaCb.current === resolve) { pendingAriaCb.current = null; resolve(null); } }, 5000);
    });
  }, [bridge]);

  /** Request a fresh screenshot from the extension, resolving via pending callback. */
  const requestScreenshot = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      lastBridgeErrorRef.current = null;
      pendingScreenshotCb.current = resolve;
      bridge.send({ type: 'CAPTURE_SCREENSHOT' });
      setTimeout(() => { if (pendingScreenshotCb.current === resolve) { pendingScreenshotCb.current = null; resolve(null); } }, 5000);
    });
  }, [bridge]);

  /** Tier-2: run triageFailure for a specific failed step. */
  const handleRequestAiTriage = useCallback(async (testId: string, step: IRStep) => {
    const settings = loadSettings();
    if (!settings.tier2Enabled) return;

    const stepId = step.id;
    // Mark as loading
    setAiTriageResults(prev => {
      const next = new Map(prev);
      next.set(stepId, { status: 'loading', text: '' });
      return next;
    });

    try {
      const [ariaTree, screenshotDataUrl] = await Promise.all([
        requestAriaTree(),
        requestScreenshot(),
      ]);

      if (!screenshotDataUrl) {
        const reason = lastBridgeErrorRef.current ?? 'no response from the extension — is a tab attached?';
        setAiTriageResults(prev => { const n = new Map(prev); n.set(stepId, { status: 'error', text: `Could not capture screenshot: ${reason}` }); return n; });
        return;
      }

      const label = (step as { label?: string }).label ?? step.type;
      const sel   = ('selector' in step && step.selector)
        ? (step.selector as { candidates: Array<{ value: string }> }).candidates[0]?.value
        : undefined;

      await triageFailure(
        { endpoint: settings.ollamaEndpoint, model: settings.ollamaModel },
        {
          screenshotDataUrl,
          ariaTree,
          failedStepType:     step.type,
          failedStepLabel:    label,
          failedStepSelector: sel,
          onChunk: (chunk) => {
            setAiTriageResults(prev => {
              const n = new Map(prev);
              const cur = n.get(stepId) ?? { status: 'loading' as const, text: '' };
              n.set(stepId, { status: 'loading', text: cur.text + chunk });
              return n;
            });
          },
        },
      );

      setAiTriageResults(prev => {
        const n   = new Map(prev);
        const cur = n.get(stepId) ?? { status: 'done' as const, text: '' };
        n.set(stepId, { status: 'done', text: cur.text });
        return n;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAiTriageResults(prev => { const n = new Map(prev); n.set(stepId, { status: 'error', text: msg }); return n; });
    }
  }, [requestAriaTree, requestScreenshot]);

  /** Tier-2: run draftSteps for the current page in the recorder. */
  const handleRequestDraftSteps = useCallback(async () => {
    const settings = loadSettings();
    if (!settings.tier2Enabled) return;

    setDraftStepsResult({ status: 'loading', text: '' });

    try {
      const [ariaTree, screenshotDataUrl] = await Promise.all([
        requestAriaTree(),
        requestScreenshot(),
      ]);

      if (!screenshotDataUrl) {
        const reason = lastBridgeErrorRef.current ?? 'no response from the extension — is a tab attached?';
        setDraftStepsResult({ status: 'error', text: `Could not capture screenshot: ${reason}` });
        return;
      }

      await draftSteps(
        { endpoint: settings.ollamaEndpoint, model: settings.ollamaModel },
        {
          screenshotDataUrl,
          ariaTree,
          pageTitle: activeTabTitle || undefined,
          onChunk: (chunk) => {
            setDraftStepsResult(prev => ({ status: 'loading', text: prev.text + chunk }));
          },
        },
      );

      setDraftStepsResult(prev => ({ status: 'done', text: prev.text }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDraftStepsResult({ status: 'error', text: msg });
    }
  }, [requestAriaTree, requestScreenshot, activeTabTitle]);

  const handleRunFromLibrary = useCallback((loadedSteps: IRStep[], testId: string, testName: string) => {
    setSteps(loadedSteps);
    setStepStatuses({});
    setLoadedTestId(testId);
    setLoadedTestName(testName);
    setActiveStudioTab('record');
    setTimeout(() => void handleReplayInner(loadedSteps), 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTestSaved = useCallback((testId: string, testName: string) => {
    setLoadedTestId(testId);
    setLoadedTestName(testName);
    setShowSaveModal(false);
  }, []);

  const updateLastRunStatus = useCallback(async (statuses: Record<string, 'ok' | 'not_found' | 'error'>) => {
    if (!loadedTestId) return;
    const hasFailure = Object.values(statuses).some(s => s === 'not_found' || s === 'error');
    await db.tests.update(loadedTestId, { lastRunAt: Date.now(), lastRunStatus: hasFailure ? 'failed' : 'passed' });
  }, [loadedTestId]);

  // Internal replay loop (shared by recorder replay + handleRunFromLibrary)
  const handleReplayInner = useCallback(async (stepsToReplay: IRStep[]) => {
    if (stepsToReplay.length === 0 || activeTabId < 0) return;
    setStepStatuses({});
    // Clear any screenshot overlay from a previous run so the mirror is live
    setStepScreenshotUrl(null);
    setStepScreenshotLabel(undefined);
    const finalStatuses: Record<string, 'ok' | 'not_found' | 'error'> = {};

    for (let i = 0; i < stepsToReplay.length; i++) {
      const step = stepsToReplay[i];
      if (!step) continue;
      setReplayingIndex(i);

      const result = await new Promise<'ok' | 'not_found' | 'error'>((resolve) => {
        pendingStepRef.current.set(step.id, resolve);
        bridge.send({ type: 'REPLAY_IR_STEP', stepId: step.id, stepJson: JSON.stringify(step) });
        setTimeout(() => {
          if (pendingStepRef.current.has(step.id)) { pendingStepRef.current.delete(step.id); resolve('error'); }
        }, RECORDER_STEP_TIMEOUT_MS);
      });

      finalStatuses[step.id] = result;
      await new Promise<void>((res) => setTimeout(res, 300));
    }

    setReplayingIndex(null);
    void updateLastRunStatus(finalStatuses);
  }, [bridge, activeTabId, updateLastRunStatus]);

  const handleReplay = useCallback(async () => {
    await handleReplayInner(steps);
  }, [steps, handleReplayInner]);

  const handleSave = useCallback(() => {
    const ir = _buildTestIR(loadedTestName || 'Recorded test', steps);
    const blob = new Blob([JSON.stringify(ir, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'plex-test.json'; a.click();
    URL.revokeObjectURL(url);
  }, [steps, loadedTestName]);

  const handleAddNetworkAssert = useCallback((event: NetworkEvent) => {
    const step = {
      id:   newId(),
      type: 'networkAssert' as const,
      label: `${event.method} ${new URL(event.url).pathname} → ${event.status}`,
      method: event.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      urlPattern: event.url,
      expectedStatus: event.status,
    };
    setSteps(prev => [...prev, step as unknown as IRStep]);
    setLoadedTestId(null);
  }, []);

  const handleClearNetworkEvents = useCallback(() => setNetworkEvents([]), []);

  const handleClear    = useCallback(() => setSteps([]), []);
  const handleNewTest  = useCallback(() => { setSteps([]); setStepStatuses({}); setLoadedTestId(null); setLoadedTestName(''); setEditingFunctionId(null); setEditingFunctionName(''); }, []);
  const handleLoad     = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const ir = JSON.parse(await file.text()) as TestIR;
        setSteps((ir.flows[0]?.steps ?? []) as IRStep[]);
        setStepStatuses({});
        setLoadedTestId(null);
        setLoadedTestName(ir.meta.name || file.name.replace('.json', ''));
      } catch { alert('Invalid IR JSON file.'); }
    };
    input.click();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Execution state machine
  // ─────────────────────────────────────────────────────────────────────────

  // Step 1: Called from TestLibrary "Run Selected" → show Run Config Sheet
  const handleRunSelected = useCallback((tests: DBTest[]) => {
    setPendingRunTests(tests);
    setShowRunConfigSheet(true);
  }, []);

  // Step 2: Called when user confirms the Run Config Sheet → build queue + go to Execution
  const handleConfirmRunConfig = useCallback((config: RunConfig) => {
    runConfigRef.current = config;
    setShowRunConfigSheet(false);

    const queueItems: ExecutionQueueItem[] = pendingRunTests.map((test) => {
      let steps: IRStep[] = [];
      try {
        const ir = JSON.parse(test.irJson) as TestIR;
        steps = (ir.flows[0]?.steps ?? []) as IRStep[];
      } catch { /* corrupt IR — empty steps */ }
      return {
        testId:         test.id,
        testName:       test.name,
        tags:           test.tags ?? [],
        steps,
        status:         'pending',
        stepStatuses:   {},
        currentStepIdx: null,
        startedAt:      null,
        finishedAt:     null,
      };
    });

    setExecutionQueue(queueItems);
    setExecutionStatus('idle');
    setExecutionPassedTests(0);
    setExecutionFailedTests(0);
    executionStopRef.current  = false;
    executionPauseRef.current = false;
    setActiveStudioTab('execution');
  }, [pendingRunTests]);

  const handleExecutionPlay = useCallback(async () => {
    // Resume from pause
    if (executionPauseRef.current) {
      executionPauseRef.current = false;
      setExecutionStatus('running');
      return;
    }

    // Start fresh (only if loop not already running)
    if (executionLoopActiveRef.current) return;
    executionLoopActiveRef.current = true;
    executionStopRef.current  = false;
    executionPauseRef.current = false;
    setExecutionStatus('running');
    // Clear previous run's screenshot so the mirror doesn't show a stale frame
    // while waiting for the first CAPTURE_SCREENSHOT of this new run
    setStepScreenshotUrl(null);
    setStepScreenshotLabel(undefined);

    // Capture snapshot of queue at start time
    // We need the current queue synchronously. Because setState is async,
    // we read the queue via a callback to get the latest value.
    let startQueue: ExecutionQueueItem[] = [];
    setExecutionQueue(prev => { startQueue = prev; return prev; });
    // Wait one tick for startQueue to be populated
    await new Promise<void>(res => setTimeout(res, 0));

    const runResults: Array<{
      testId: string; testName: string;
      status: 'passed' | 'failed' | 'pending';
      stepResults: Record<string, 'ok' | 'not_found' | 'error'>;
      startedAt: number; finishedAt: number;
    }> = startQueue.map(item => ({
      testId: item.testId, testName: item.testName,
      status: 'pending', stepResults: {}, startedAt: 0, finishedAt: 0,
    }));

    let overallPassed = 0;
    let overallFailed = 0;

    for (let ti = 0; ti < startQueue.length; ti++) {
      if (executionStopRef.current) break;

      const item = startQueue[ti];
      runResults[ti].startedAt = Date.now();

      setExecutionQueue(prev => prev.map((e, i) =>
        i === ti ? { ...e, status: 'running', startedAt: runResults[ti].startedAt, currentStepIdx: 0 } : e
      ));

      let testFailed = false;

      for (let si = 0; si < item.steps.length; si++) {
        // Pause gate
        while (executionPauseRef.current && !executionStopRef.current) {
          await new Promise<void>(res => setTimeout(res, 100));
        }
        if (executionStopRef.current) { testFailed = true; break; }

        const step = item.steps[si];
        if (!step) continue;

        setExecutionQueue(prev => prev.map((e, i) => i === ti ? { ...e, currentStepIdx: si } : e));

        const cfg = runConfigRef.current;
        const result = await new Promise<'ok' | 'not_found' | 'error'>((resolve) => {
          pendingStepRef.current.set(step.id, resolve);
          bridge.send({ type: 'REPLAY_IR_STEP', stepId: step.id, stepJson: JSON.stringify(step) });
          setTimeout(() => {
            if (pendingStepRef.current.has(step.id)) { pendingStepRef.current.delete(step.id); resolve('error'); }
          }, cfg.stepTimeoutMs);
        });

        runResults[ti].stepResults[step.id] = result;

        setExecutionQueue(prev => prev.map((e, i) =>
          i === ti ? { ...e, stepStatuses: { ...e.stepStatuses, [step.id]: result } } : e
        ));

        // For extension-based runs: request a screenshot after each step so the
        // mirror shows exactly what the browser looked like when the step finished.
        if (runConfigRef.current.target === 'studio') {
          bridge.send({ type: 'CAPTURE_SCREENSHOT' });
          const stepLabel_ = (step as { label?: string }).label ?? step.type;
          setStepScreenshotLabel(`Step ${si + 1}/${item.steps.length} — ${step.type} ${stepLabel_}`);
        }

        if (result !== 'ok' && cfg.stopOnFirstFailure) { testFailed = true; break; }
        if (result !== 'ok') testFailed = true;

        await new Promise<void>(res => setTimeout(res, cfg.stepDelayMs));
      }

      if (executionStopRef.current) break;

      runResults[ti].status     = testFailed ? 'failed' : 'passed';
      runResults[ti].finishedAt = Date.now();

      if (testFailed) overallFailed++;
      else overallPassed++;

      setExecutionQueue(prev => prev.map((e, i) =>
        i === ti ? { ...e, status: testFailed ? 'failed' : 'passed', currentStepIdx: null, finishedAt: runResults[ti].finishedAt } : e
      ));

      await db.tests.update(item.testId, {
        lastRunAt: Date.now(),
        lastRunStatus: testFailed ? 'failed' : 'passed',
      });
    }

    executionLoopActiveRef.current = false;

    if (!executionStopRef.current) {
      setExecutionPassedTests(overallPassed);
      setExecutionFailedTests(overallFailed);
      setExecutionStatus('done');

      // Build run record
      const startMs  = runResults[0]?.startedAt  ?? Date.now();
      const endMs    = runResults[runResults.length - 1]?.finishedAt ?? Date.now();
      const runTests: DBRunTest[] = runResults
        .filter(r => r.status !== 'pending')
        .map((r, ri): DBRunTest => {
          const qItem = startQueue[ri];
          const steps: DBRunStep[] = (qItem?.steps ?? []).map(s => ({
            stepId:    s.id,
            stepType:  s.type,
            label:     (s as { label?: string }).label,
            status:    r.stepResults[s.id] ?? 'skipped',
            durationMs: 0,
          }));
          const passedSteps = steps.filter(st => st.status === 'ok').length;
          const s = r.status === 'passed' || r.status === 'failed' ? r.status : 'failed';
          return {
            testId:      r.testId,
            testName:    r.testName,
            status:      s,
            steps,
            startedAt:   r.startedAt,
            finishedAt:  r.finishedAt,
            tags:        qItem?.tags ?? [],
            passedSteps,
            healCount:   0,
          };
        });

      if (runTests.length > 0) {
        const run: DBRun = {
          id:              newId(),
          runAt:           Date.now(),
          status:          overallFailed > 0 ? 'failed' : 'passed',
          tests:           runTests,
          totalDurationMs: endMs - startMs,
          passedCount:     overallPassed,
          failedCount:     overallFailed,
          browser:         runConfigRef.current?.browser ?? 'chromium',
          healCount:       0,
        };
        await db.runs.add(run);
      }
    }
  }, [bridge]);

  const handleExecutionPause = useCallback(() => {
    executionPauseRef.current = true;
    setExecutionStatus('paused');
  }, []);

  const handleExecutionStop = useCallback(() => {
    executionStopRef.current = true;
    executionPauseRef.current = false;
    executionLoopActiveRef.current = false;
    setExecutionStatus('done');
  }, []);

  const handleExecutionReset = useCallback(() => {
    executionStopRef.current  = false;
    executionPauseRef.current = false;
    executionLoopActiveRef.current = false;
    setExecutionQueue([]);
    setExecutionStatus('idle');
    setExecutionPassedTests(0);
    setExecutionFailedTests(0);
    setStepScreenshotUrl(null);
    setStepScreenshotLabel(undefined);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Layout helpers
  // ─────────────────────────────────────────────────────────────────────────

  const showDoctor    = bridgeStatus === 'error' || (!isExtensionAvailable() && bridgeStatus !== 'connected');
  const showMirrorTab = activeStudioTab === 'record' || activeStudioTab === 'execution';
  const showFullWidth = activeStudioTab === 'tests' || activeStudioTab === 'reports' || activeStudioTab === 'settings' || activeStudioTab === 'functions';

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Modals */}
      {showTabPicker && <TabPicker tabs={tabs} onPick={handleTabPick} onClose={() => setShowTabPicker(false)} />}
      {showSaveModal && <SaveTestModal steps={steps} onSaved={handleTestSaved} onClose={() => setShowSaveModal(false)} />}
      {showRunConfigSheet && (
        <RunConfigSheet
          tests={pendingRunTests}
          initial={runConfigRef.current}
          onRun={handleConfirmRunConfig}
          onCancel={() => setShowRunConfigSheet(false)}
        />
      )}

      {/* Left nav */}
      <LeftNav activeTab={activeStudioTab} onTabChange={setActiveStudioTab} />

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Full-width tabs — tests + reports (no mirror) */}
        {activeStudioTab === 'tests' && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <TestLibrary
              onLoad={handleLoadFromLibrary}
              onRun={handleRunFromLibrary}
              onRunSelected={handleRunSelected}
            />
          </div>
        )}
        {activeStudioTab === 'reports' && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <ReportsPage />
          </div>
        )}
        {activeStudioTab === 'settings' && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SettingsPage />
          </div>
        )}
        {activeStudioTab === 'functions' && (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <FunctionsPage onEditInRecorder={handleEditFunctionInRecorder} />
          </div>
        )}

        {/* Mirror panel — always mounted; hidden on tests/reports to keep frames alive */}
        <div style={{
          flex: '0 0 50%',
          display: showMirrorTab ? 'flex' : 'none',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}>
          {/* Mirror header */}
          <div style={{
            padding: '8px 14px',
            borderBottom: '1px solid #27272a',
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#0d0d10', flexShrink: 0,
          }}>
            {/* Relay mode: show Playwright screenshot header (no extension mirror) */}
            {activeStudioTab === 'execution' && relayStatus === 'connected' && !isMirroring ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#a1a1aa' }}>
                  Mirror: Playwright · {runConfigRef.current.browser}
                  {runConfigRef.current.headless ? ' · headless' : ''}
                </span>
                <span style={{ fontSize: 11, color: '#7c3aed', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {stepScreenshotLabel ?? 'Waiting for step screenshots…'}
                </span>
                <button
                  onClick={handleDisconnectRelay}
                  style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '4px 12px', color: '#a1a1aa', cursor: 'pointer', fontSize: 12 }}
                >Disconnect</button>
              </>
            ) : isMirroring ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#a1a1aa' }}>
                  {activeStudioTab === 'execution' && stepScreenshotUrl
                    ? 'Mirror: Step Snapshot'
                    : 'Mirror: Streaming'}
                </span>
                {activeStudioTab === 'execution' && stepScreenshotUrl ? (
                  <span style={{ fontSize: 11, color: '#7c3aed', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stepScreenshotLabel}
                  </span>
                ) : (
                  <>
                    <span style={{ fontSize: 12, color: '#52525b' }}>→</span>
                    <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>{frameCount} frames</span>
                  </>
                )}
                <button
                  onClick={handleStopMirror}
                  style={{ background: '#7f1d1d', border: 'none', borderRadius: 6, padding: '4px 12px', color: '#fca5a5', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >Stop Mirror</button>
                {/* ── Highlighter controls ── */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: highlighterEnabled ? '#a1a1aa' : '#52525b', marginLeft: 4 }}>
                  <input type="checkbox" checked={highlighterEnabled}
                    onChange={e => setHighlighterEnabled(e.target.checked)}
                    style={{ accentColor: highlighterColor, width: 12, height: 12 }}
                  />
                  Highlighter
                </label>
                <input type="color" value={highlighterColor}
                  onChange={e => setHighlighterColor(e.target.value)}
                  title="Highlighter color"
                  disabled={!highlighterEnabled}
                  style={{
                    width: 22, height: 22, border: '1px solid #3f3f46', padding: 1,
                    borderRadius: 4, cursor: highlighterEnabled ? 'pointer' : 'not-allowed',
                    opacity: highlighterEnabled ? 1 : 0.4, background: 'none',
                  }}
                />
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, color: '#52525b' }}>Mirror: Not streaming</span>
                <button
                  onClick={handleStartMirror}
                  disabled={bridgeStatus !== 'connected'}
                  style={{
                    background: bridgeStatus === 'connected' ? '#052e16' : '#1c1c1f',
                    border: `1px solid ${bridgeStatus === 'connected' ? '#166534' : '#3f3f46'}`,
                    borderRadius: 6, padding: '4px 14px',
                    color: bridgeStatus === 'connected' ? '#86efac' : '#52525b',
                    cursor: bridgeStatus === 'connected' ? 'pointer' : 'not-allowed',
                    fontSize: 12, fontWeight: 600,
                  }}
                >Start Mirror</button>
              </>
            )}
            <div style={{ flex: 1 }} />
            {bridgeStatus !== 'connected' && (
              <button onClick={handleConnect} style={{ background: '#7c3aed', border: 'none', borderRadius: 6, padding: '4px 14px', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Connect Browser
              </button>
            )}
          </div>

          {/* Mirror canvas */}
          {showDoctor ? (
            <ConnectionDoctor onRetry={() => bridge.connect()} />
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 14px' }}>
              <BrowserMirror
                bridge={bridge}
                isRecording={isRecording}
                isMirroring={isMirroring}
                isAttaching={isAttaching}
                attachingTitle={attachingTitle}
                captureWidth={captureSize.width}
                captureHeight={captureSize.height}
                staticImageUrl={
                  activeStudioTab === 'execution' && stepScreenshotUrl ? stepScreenshotUrl : null
                }
                staticImageLabel={
                  activeStudioTab === 'execution' ? stepScreenshotLabel : undefined
                }
                captureMode={commandCaptureActive}
                mode={mode}
                highlighterEnabled={highlighterEnabled}
                highlighterColor={highlighterColor}
                elementCapturedBounds={elementCapturedBounds}
              />
            </div>
          )}
        </div>

        {/* Record panel (right 50%) */}
        {activeStudioTab === 'record' && (
          <RecorderPanel
            bridge={bridge}
            bridgeStatus={bridgeStatus}
            activeTabTitle={activeTabTitle}
            activeTabId={activeTabId}
            mode={mode}
            isRecording={isRecording}
            steps={steps}
            replayingIndex={replayingIndex}
            stepStatuses={stepStatuses}
            unsaved={unsaved}
            loadedTestName={loadedTestName}
            editingFunctionName={editingFunctionName || undefined}
            onSaveFunction={editingFunctionId ? () => void handleSaveFunction() : undefined}
            tier2Enabled={loadSettings().tier2Enabled}
            draftStepsResult={draftStepsResult}
            onRequestDraftSteps={() => void handleRequestDraftSteps()}
            onSetMode={handleSetMode}
            onToggleRecord={handleToggleRecord}
            onNewTest={handleNewTest}
            onClear={handleClear}
            onSaveTest={() => setShowSaveModal(true)}
            onLoad={handleLoad}
            onDownload={handleSave}
            networkEvents={networkEvents}
            onDeleteStep={handleDelete}
            onEditStep={handleEditStep}
            onEditCandidates={handleEditCandidates}
            onReorderStep={handleReorderStep}
            onAddSteps={handleAddSteps}
            onReplay={() => void handleReplay()}
            onAddNetworkAssert={handleAddNetworkAssert}
            onClearNetworkEvents={handleClearNetworkEvents}
            onRequestCapture={handleRequestCapture}
            captureActive={commandCaptureActive}
            inspectedNode={inspectedNode}
            aiAssistEnabled={loadSettings().aiAssistEnabled}
            assistModeActive={assistModeActive}
            onSetAssistMode={setAssistModeActive}
            onRequestTree={requestAriaTree}
          />
        )}

        {/* Execution panel (right 50%) */}
        {activeStudioTab === 'execution' && (
          <ExecutionPanel
            queue={executionQueue}
            runStatus={executionStatus}
            passedTests={executionPassedTests}
            failedTests={executionFailedTests}
            runConfig={runConfigRef.current}
            relayStatus={relayStatus}
            currentStepLabel={stepScreenshotLabel}
            tier2Enabled={loadSettings().tier2Enabled}
            aiTriageResults={aiTriageResults}
            onRequestAiTriage={(testId, step) => void handleRequestAiTriage(testId, step)}
            onPlay={() => void handleExecutionPlay()}
            onPause={handleExecutionPause}
            onStop={handleExecutionStop}
            onReset={handleExecutionReset}
            onViewReports={() => setActiveStudioTab('reports')}
            onConnectRelay={handleConnectRelay}
            onDisconnectRelay={handleDisconnectRelay}
          />
        )}
      </div>
    </div>
  );
}
